import { Loader2 } from "lucide-react"

interface VideoPlayerProps {
  streamEmbedUrl: string | null
  videoUrl: string | null
  streamStatus: "pending" | "processing" | "ready" | "error"
}

function getYouTubeEmbedUrl(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&?/\s]+)/,
  )
  return match
    ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&playsinline=1`
    : null
}

export default function VideoPlayer({
  streamEmbedUrl,
  videoUrl,
  streamStatus,
}: VideoPlayerProps) {
  if (streamStatus === "processing") {
    return (
      <div className="bg-secondary flex aspect-video items-center justify-center rounded-lg">
        <div className="text-muted-foreground flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-sm">Procesando video...</span>
        </div>
      </div>
    )
  }

  if (streamStatus === "error") {
    return (
      <div className="bg-secondary flex aspect-video items-center justify-center rounded-lg">
        <span className="text-sm text-red-400">Error al procesar el video</span>
      </div>
    )
  }

  if (streamEmbedUrl && streamStatus === "ready") {
    return (
      <div className="aspect-video rounded-lg">
        <iframe
          src={streamEmbedUrl}
          title="Video player"
          className="h-full w-full rounded-lg"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    )
  }

  if (videoUrl) {
    const youtubeEmbedUrl = getYouTubeEmbedUrl(videoUrl)
    if (youtubeEmbedUrl) {
      return (
        <div className="aspect-video rounded-lg">
          <iframe
            src={youtubeEmbedUrl}
            title="Video player"
            className="h-full w-full rounded-lg"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      )
    }
  }

  return (
    <div className="aspect-video rounded-lg">
      <video
        data-testid="native-video-player"
        src={videoUrl ?? undefined}
        controls
        autoPlay
        playsInline
        className="h-full w-full rounded-lg"
      />
    </div>
  )
}
